import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import CurrencySymbol from '@/components/CurrencySymbol';
import { fmtNum } from '@/utils/currency';
import { 
  Package, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle,
  Upload,
  Plus,
  Home,
  LogOut,
  User,
  Phone,
  Mail,
  Briefcase,
  AlertCircle,
  Edit,
  Lock,
  Users,
  Shield,
  Key,
  Eye,
  Building,
  MapPin,
  Package2
} from 'lucide-react';

export default function CustomerDashboard() {
  const { token, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingInvoicesCount, setPendingInvoicesCount] = useState(0);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showResetUserPassword, setShowResetUserPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [proofDocument, setProofDocument] = useState(null);
  const [showViewOrder, setShowViewOrder] = useState(false);
  const [showEditOrder, setShowEditOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [poDocument, setPoDocument] = useState(null);
  const [uploadingPO, setUploadingPO] = useState(false);
  const [orderEditData, setOrderEditData] = useState({
    po_number: '',
    po_document_url: null
  });
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    role: '',
    contact_number: '',
    company_email: '',
    proof_document_url: null
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [newUserData, setNewUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    contact_name: '',
    contact_number: ''
  });
  const [resetPasswordData, setResetPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  const hasProofDocument = user?.delivery_details?.proof_document_url;
  const proofVerificationStatus = user?.delivery_details?.proof_verification_status || 'Pending';
  const isProofVerified = proofVerificationStatus === 'Verified';
  const isAdmin = user?.is_company_admin || false;

  const getVerificationBadge = () => {
    if (!hasProofDocument) return null;
    
    const configs = {
      Pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, text: 'Verifying' },
      Verified: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, text: 'Completed ✓' },
      Rejected: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, text: 'Rejected' }
    };

    const config = configs[proofVerificationStatus] || configs.Pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.text}
      </span>
    );
  };

  useEffect(() => {
    if (user?.user_type === 'customer') {
      fetchOrders();
      fetchCompanyUsers();
      fetchPendingInvoicesCount();
      // Load existing profile data
      if (user.delivery_details) {
        setProfileData({
          first_name: user.delivery_details.first_name || '',
          last_name: user.delivery_details.last_name || '',
          role: user.delivery_details.role || '',
          contact_number: user.delivery_details.contact_number || '',
          company_email: user.delivery_details.company_email || '',
          proof_document_url: user.delivery_details.proof_document_url || null
        });
      }
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/customer/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      const response = await axios.get(`${API}/company/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch company users');
    }
  };

  const fetchPendingInvoicesCount = async () => {
    try {
      const response = await axios.get(`${API}/customer/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Customer invoices:', response.data);
      const pendingCount = response.data.filter(inv => inv.payment_status === 'pending').length;
      console.log('Pending invoices count:', pendingCount);
      setPendingInvoicesCount(pendingCount);
    } catch (error) {
      console.error('Failed to fetch invoices count:', error);
    }
  };

  const handleCreateRFQ = () => {
    if (!hasProofDocument) {
      toast.error('⚠️ Please upload your Proof of Role to create RFQs');
      setShowEditProfile(true);
      return;
    }
    
    if (proofVerificationStatus === 'Pending') {
      toast.error('⚠️ Your Proof of Role is being verified. Please wait for admin approval.');
      return;
    }
    
    if (proofVerificationStatus === 'Rejected') {
      const rejectionReason = user?.delivery_details?.proof_rejection_reason || 'Document rejected by admin';
      toast.error(`⚠️ Your Proof of Role was rejected. Reason: ${rejectionReason}. Please upload a new document.`);
      setShowEditProfile(true);
      return;
    }
    
    navigate('/');
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowViewOrder(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setOrderEditData({
      po_number: order.po_number || '',
      po_document_url: order.po_document_url || null
    });
    setShowEditOrder(true);
  };

  const handlePOFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must not exceed 10MB');
      return;
    }

    setPoDocument(file);
    setUploadingPO(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/proof-document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setOrderEditData({
        ...orderEditData,
        po_document_url: response.data.document_url
      });
      toast.success('PO document uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PO document');
      setPoDocument(null);
    } finally {
      setUploadingPO(false);
    }
  };

  const handleUpdateOrder = async () => {
    if (!orderEditData.po_number || !orderEditData.po_document_url) {
      toast.error('Both PO Number and PO Document are required');
      return;
    }

    try {
      await axios.put(
        `${API}/customer/orders/${selectedOrder.id}`,
        {
          po_number: orderEditData.po_number,
          po_document_url: orderEditData.po_document_url
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Order updated successfully!');
      setShowEditOrder(false);
      setSelectedOrder(null);
      setPoDocument(null);
      fetchOrders(); // Refresh orders list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    const maxSize = 8 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must not exceed 8MB');
      return;
    }

    setProofDocument(file);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/proof-document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Set status to Pending when new document is uploaded
      setProfileData({
        ...profileData,
        proof_document_url: response.data.document_url,
        proof_verification_status: 'Pending',
        proof_rejection_reason: null
      });
      toast.success('Document uploaded successfully. Awaiting admin verification.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
      setProofDocument(null);
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    const contactNumber = profileData.contact_number.trim();
    if (contactNumber.length !== 7) {
      toast.error('⚠️ Contact number must be exactly 7 digits');
      return;
    }
    if (!contactNumber.startsWith('7') && !contactNumber.startsWith('9')) {
      toast.error('⚠️ Write your mobile number (must start with 7 or 9)');
      return;
    }

    try {
      await axios.put(`${API}/customer/delivery-details`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Profile updated successfully!');
      setShowEditProfile(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      await axios.post(`${API}/customer/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/company/users`, newUserData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User created successfully!');
      setShowCreateUser(false);
      setNewUserData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        contact_name: '',
        contact_number: ''
      });
      fetchCompanyUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleResetUserPassword = async (e) => {
    e.preventDefault();

    if (resetPasswordData.new_password !== resetPasswordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetPasswordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      await axios.post(`${API}/company/users/${selectedUser.id}/reset-password`, {
        new_password: resetPasswordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Password reset successfully!');
      setShowResetUserPassword(false);
      setResetPasswordData({ new_password: '', confirm_password: '' });
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Open': { color: 'bg-blue-100 text-blue-700', icon: Clock },
      'Processing': { color: 'bg-yellow-100 text-yellow-700', icon: Package },
      'Completed': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'Cancelled': { color: 'bg-red-100 text-red-700', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig['Open'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-slate-900">
                {isAdmin && <Shield className="w-6 h-6 text-emerald-600 inline mr-2" />}
                My Dashboard
              </h1>
              {isAdmin && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">Admin</span>}
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/customer-invoices')}
                variant="outline"
                className="flex items-center gap-2 relative"
              >
                <FileText className="w-4 h-4" />
                My Invoices
                {pendingInvoicesCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {pendingInvoicesCount}
                  </span>
                )}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Website
              </Button>
              <Button
                onClick={() => logout()}
                variant="ghost"
                className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Proof Document Alerts */}
        {!hasProofDocument && (
          <Card className="mb-6 p-4 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">Action Required</h3>
                <p className="text-sm text-amber-700 mb-3">
                  Please upload your Proof of Role document to create RFQs and place orders.
                </p>
                <Button
                  onClick={() => setShowEditProfile(true)}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Now
                </Button>
              </div>
            </div>
          </Card>
        )}

        {hasProofDocument && proofVerificationStatus === 'Pending' && (
          <Card className="mb-6 p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Document Under Review</h3>
                <p className="text-sm text-blue-700">
                  Your Proof of Role document is being verified by Assetco admin. You'll be notified once the verification is complete.
                </p>
              </div>
            </div>
          </Card>
        )}

        {hasProofDocument && proofVerificationStatus === 'Rejected' && (
          <Card className="mb-6 p-4 bg-red-50 border-red-200">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">Document Rejected</h3>
                <p className="text-sm text-red-700 mb-2">
                  <strong>Reason:</strong> {user?.delivery_details?.proof_rejection_reason || 'Document does not meet requirements'}
                </p>
                <p className="text-sm text-red-700 mb-3">
                  Please upload a new document that meets the requirements.
                </p>
                <Button
                  onClick={() => setShowEditProfile(true)}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload New Document
                </Button>
              </div>
            </div>
          </Card>
        )}

        {hasProofDocument && proofVerificationStatus === 'Verified' && (
          <Card className="mb-6 p-4 bg-green-50 border-green-200">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 mb-1">Document Verified ✓</h3>
                <p className="text-sm text-green-700">
                  Your Proof of Role has been verified. You can now create RFQs and place orders.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Admin User Management Section */}
        {isAdmin && (
          <Card className="mb-6 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Company Users ({companyUsers.length}/3)
                </h2>
                <p className="text-sm text-slate-600 mt-1">Manage users in your company</p>
              </div>
              {companyUsers.length < 3 && (
                <Button
                  onClick={() => setShowCreateUser(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              )}
            </div>
            <div className="grid gap-3">
              {companyUsers.map(usr => (
                <div key={usr.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      usr.is_company_admin ? 'bg-emerald-100' : 'bg-blue-100'
                    }`}>
                      {usr.is_company_admin ? (
                        <Shield className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <User className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {usr.first_name} {usr.last_name}
                        {usr.is_company_admin && <span className="ml-2 text-xs text-emerald-600">(Admin)</span>}
                        {usr.id === user?.id && <span className="ml-2 text-xs text-blue-600">(You)</span>}
                      </p>
                      <p className="text-sm text-slate-600">{usr.email}</p>
                    </div>
                  </div>
                  {!usr.is_company_admin && (
                    <Button
                      onClick={() => {
                        setSelectedUser(usr);
                        setShowResetUserPassword(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Key className="w-4 h-4" />
                      Reset Password
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Profile Summary Card */}
        <Card className="mb-6 p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Profile Information</h2>
              <div className="space-y-2 text-sm">
                <p className="text-slate-600">
                  <span className="font-medium">Name:</span> {user?.delivery_details?.first_name} {user?.delivery_details?.last_name}
                </p>
                <p className="text-slate-600">
                  <span className="font-medium">Role:</span> {user?.delivery_details?.role || 'Not set'}
                </p>
                <p className="text-slate-600">
                  <span className="font-medium">Email:</span> {user?.email}
                </p>
                <p className="text-slate-600">
                  <span className="font-medium">Business:</span> {user?.business_name} ({user?.business_type})
                </p>
                <p className="text-slate-600">
                  <span className="font-medium">Proof Document:</span>{' '}
                  {hasProofDocument ? (
                    <span className="inline-flex items-center gap-2">
                      {getVerificationBadge()}
                    </span>
                  ) : (
                    <span className="text-red-600">✗ Not Uploaded</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowEditProfile(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Profile
              </Button>
              <Button
                onClick={() => setShowChangePassword(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Lock className="w-4 h-4" />
                Change Password
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total Orders</p>
                <p className="text-3xl font-bold text-slate-900">{orders.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Open Orders</p>
                <p className="text-3xl font-bold text-slate-900">
                  {orders.filter(o => o.status === 'Open').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Completed Orders</p>
                <p className="text-3xl font-bold text-slate-900">
                  {orders.filter(o => o.status === 'Completed').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Create New RFQ Button */}
        <div className="mb-6">
          <Button
            onClick={handleCreateRFQ}
            className="w-full h-16 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-lg font-semibold"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New RFQ
          </Button>
        </div>

        {/* My Invoices Section */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-600 rounded-lg relative">
                <FileText className="w-6 h-6 text-white" />
                {pendingInvoicesCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                    {pendingInvoicesCount}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  My Invoices
                  {pendingInvoicesCount > 0 && (
                    <span className="ml-2 text-sm font-normal text-red-600">
                      ({pendingInvoicesCount} pending payment)
                    </span>
                  )}
                </h2>
                <p className="text-sm text-slate-600">View and manage your credit invoices</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/customer-invoices')}
              className="bg-blue-600 hover:bg-blue-700 relative"
            >
              View All Invoices
              {pendingInvoicesCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingInvoicesCount}
                </span>
              )}
            </Button>
          </div>
        </Card>

        {/* Orders List */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">
            {isAdmin ? 'All Company Orders' : 'My Orders'}
          </h2>
          {orders.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No orders yet</p>
              <p className="text-sm text-slate-500 mt-2">
                Create your first RFQ to get started
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <Card key={order.id} className="p-4 border-slate-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900">{order.order_number}</h3>
                        {getStatusBadge(order.status)}
                        {(!order.po_number || !order.po_document_url) && (
                          <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                            <AlertCircle className="w-3 h-3" />
                            PO Required for Processing
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        Created by: {order.customer_name} • {order.items?.length || 0} item(s) • {new Date(order.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm font-medium text-slate-900 mt-1">
                        Total: {order.currency === 'MVR' ? (
                          <><CurrencySymbol currency="MVR" /> {fmtNum(order.total || 0)}</>
                        ) : (
                          `$${fmtNum(order.total || 0)}`
                        )}
                      </p>
                      {(!order.po_number || !order.po_document_url) && (
                        <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Please add PO Number and PO Document to process this order
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewOrder(order)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleEditOrder(order)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="w-4 h-4" />
                        Edit PO
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4" />
                First Name *
              </Label>
              <Input
                value={profileData.first_name}
                onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                required
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4" />
                Last Name (Including Middle Name) *
              </Label>
              <Input
                value={profileData.last_name}
                onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                required
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4" />
                Role *
              </Label>
              <Input
                value={profileData.role}
                onChange={(e) => setProfileData({...profileData, role: e.target.value})}
                required
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4" />
                Contact Number *
              </Label>
              <Input
                value={profileData.contact_number}
                onChange={(e) => setProfileData({...profileData, contact_number: e.target.value})}
                maxLength={7}
                pattern="[79][0-9]{6}"
                required
              />
              <p className="text-xs text-slate-500 mt-1">7 digits, starts with 7 or 9</p>
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4" />
                Company Email *
              </Label>
              <Input
                type="email"
                value={profileData.company_email}
                onChange={(e) => setProfileData({...profileData, company_email: e.target.value})}
                required
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4" />
                Proof of Role {!profileData.proof_document_url && '*'}
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-emerald-500 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="proof-upload-edit"
                  disabled={uploading}
                />
                <label htmlFor="proof-upload-edit" className="cursor-pointer">
                  <Upload className={`w-6 h-6 mx-auto mb-2 ${uploading ? 'text-slate-400' : 'text-emerald-600'}`} />
                  {uploading ? (
                    <p className="text-sm text-slate-600">Uploading...</p>
                  ) : profileData.proof_document_url ? (
                    <p className="text-sm text-emerald-600 font-medium">✓ Document Uploaded</p>
                  ) : (
                    <p className="text-sm text-slate-700">Click to upload (PDF, max 8MB)</p>
                  )}
                </label>
              </div>
              {!profileData.proof_document_url && (
                <p className="text-xs text-red-600 mt-1">⚠️ Required to create RFQs</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditProfile(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={uploading}>
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password *</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>New Password *</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                required
                minLength={8}
              />
              <p className="text-xs text-slate-500 mt-1">At least 8 characters</p>
            </div>

            <div>
              <Label>Confirm New Password *</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowChangePassword(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog (Admin Only) */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Company User</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label>First Name *</Label>
              <Input
                value={newUserData.first_name}
                onChange={(e) => setNewUserData({...newUserData, first_name: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Last Name *</Label>
              <Input
                value={newUserData.last_name}
                onChange={(e) => setNewUserData({...newUserData, last_name: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                required
                minLength={8}
              />
            </div>

            <div>
              <Label>Contact Name *</Label>
              <Input
                value={newUserData.contact_name}
                onChange={(e) => setNewUserData({...newUserData, contact_name: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Contact Number *</Label>
              <Input
                value={newUserData.contact_number}
                onChange={(e) => setNewUserData({...newUserData, contact_number: e.target.value})}
                maxLength={7}
                pattern="[79][0-9]{6}"
                required
              />
              <p className="text-xs text-slate-500 mt-1">7 digits, starts with 7 or 9</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateUser(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Create User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset User Password Dialog (Admin Only) */}
      <Dialog open={showResetUserPassword} onOpenChange={setShowResetUserPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.first_name} {selectedUser?.last_name}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleResetUserPassword} className="space-y-4">
            <div>
              <Label>New Password *</Label>
              <Input
                type="password"
                value={resetPasswordData.new_password}
                onChange={(e) => setResetPasswordData({...resetPasswordData, new_password: e.target.value})}
                required
                minLength={8}
              />
              <p className="text-xs text-slate-500 mt-1">At least 8 characters</p>
            </div>

            <div>
              <Label>Confirm New Password *</Label>
              <Input
                type="password"
                value={resetPasswordData.confirm_password}
                onChange={(e) => setResetPasswordData({...resetPasswordData, confirm_password: e.target.value})}
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowResetUserPassword(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Reset Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Order Dialog */}
      <Dialog open={showViewOrder} onOpenChange={setShowViewOrder}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details - {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Order Information</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>Order Number:</strong> {selectedOrder.order_number}</p>
                    <p><strong>Status:</strong> {getStatusBadge(selectedOrder.status)}</p>
                    <p><strong>Created:</strong> {new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                    <p><strong>Currency:</strong> {selectedOrder.currency}</p>
                    <p><strong>Total:</strong> {selectedOrder.currency} {fmtNum(selectedOrder.total || 0)}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Purchase Order Details</h3>
                  <div className="space-y-1 text-sm">
                    {selectedOrder.po_number ? (
                      <p><strong>PO Number:</strong> {selectedOrder.po_number}</p>
                    ) : (
                      <p className="text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        PO Number not added
                      </p>
                    )}
                    {selectedOrder.po_document_url ? (
                      <p>
                        <strong>PO Document:</strong>{' '}
                        <a href={selectedOrder.po_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View Document
                        </a>
                      </p>
                    ) : (
                      <p className="text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        PO Document not uploaded
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Details */}
              {selectedOrder.order_delivery_details && (
                <div>
                  <h3 className="font-semibold mb-2">Delivery Details</h3>
                  <div className="space-y-1 text-sm bg-slate-50 p-3 rounded">
                    <p><strong>Method:</strong> {selectedOrder.order_delivery_details.method === 'landmark' ? 'Landmark' : 'Supply Boat'}</p>
                    {selectedOrder.order_delivery_details.method === 'landmark' ? (
                      <>
                        <p><strong>Contact:</strong> {selectedOrder.order_delivery_details.first_name} {selectedOrder.order_delivery_details.last_name}</p>
                        <p><strong>Number:</strong> {selectedOrder.order_delivery_details.contact_number}</p>
                        <p><strong>Designation:</strong> {selectedOrder.order_delivery_details.designation}</p>
                        {selectedOrder.order_delivery_details.google_map_link && (
                          <p><strong>Map:</strong> <a href={selectedOrder.order_delivery_details.google_map_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View Location</a></p>
                        )}
                      </>
                    ) : (
                      <>
                        <p><strong>Captain:</strong> {selectedOrder.order_delivery_details.captain_full_name}</p>
                        <p><strong>Captain ID:</strong> {selectedOrder.order_delivery_details.captain_id_no}</p>
                        <p><strong>Captain Number:</strong> {selectedOrder.order_delivery_details.captain_number}</p>
                        <p><strong>Dhoni:</strong> {selectedOrder.order_delivery_details.dhoni_name}</p>
                        <p><strong>Jetty:</strong> {selectedOrder.order_delivery_details.jetty}</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div>
                <h3 className="font-semibold mb-2">Order Items</h3>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between p-3 bg-slate-50 rounded">
                      <div className="flex-1">
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-slate-600">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{item.currency} {fmtNum(item.price || 0)} each</p>
                        <p className="text-sm text-slate-600">Total: {item.currency} {fmtNum((item.price || 0) * (item.quantity || 0))}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {(!selectedOrder.po_number || !selectedOrder.po_document_url) && (
                <div className="bg-red-50 border border-red-200 p-4 rounded">
                  <p className="text-red-700 font-semibold flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Required for Processing
                  </p>
                  <p className="text-sm text-red-600 mt-1">
                    Please add your PO Number and attach PO Document to proceed with order processing.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowViewOrder(false)} className="flex-1">
                  Close
                </Button>
                <Button onClick={() => {
                  setShowViewOrder(false);
                  handleEditOrder(selectedOrder);
                }} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Edit PO Details
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Order (PO Details) Dialog */}
      <Dialog open={showEditOrder} onOpenChange={setShowEditOrder}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Purchase Order Details - {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded">
              <p className="text-amber-800 font-semibold flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Required Information
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Both PO Number and PO Document are required to process your order.
              </p>
            </div>

            <div>
              <Label>Enter Your PO Number *</Label>
              <Input
                value={orderEditData.po_number}
                onChange={(e) => setOrderEditData({...orderEditData, po_number: e.target.value})}
                placeholder="e.g., PO-2024-001"
                className="mt-2"
                required
              />
            </div>

            <div>
              <Label>Attach Your PO Document *</Label>
              <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePOFileChange}
                  className="hidden"
                  id="po-upload"
                  disabled={uploadingPO}
                />
                <label htmlFor="po-upload" className="cursor-pointer flex flex-col items-center gap-3">
                  <Upload className={`w-8 h-8 ${uploadingPO ? 'text-slate-400' : 'text-emerald-600'}`} />
                  {uploadingPO ? (
                    <p className="text-slate-600">Uploading...</p>
                  ) : poDocument || orderEditData.po_document_url ? (
                    <>
                      <p className="text-emerald-600 font-medium">
                        {poDocument ? poDocument.name : '✓ PO Document Uploaded'}
                      </p>
                      <p className="text-xs text-slate-500">Click to change file</p>
                    </>
                  ) : (
                    <>
                      <p className="text-slate-700 font-medium">Click to upload PO document</p>
                      <p className="text-xs text-slate-500">PDF only, max 10MB</p>
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-red-600 mt-2">* Required for order processing</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditOrder(false);
                  setPoDocument(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateOrder}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={uploadingPO || !orderEditData.po_number || !orderEditData.po_document_url}
              >
                Save PO Details
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
